import { useState, useEffect } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Modal } from '../../../modal';
import { Button } from '../../../button';
import {
  IFindCarSaleQuery,
  useUpdateCarSaleMutation,
} from '../../../../services/carSaleServices/__generated__/queries.generated';
import { FIND_CAR_SALE } from '../../../../services/carSaleServices/queries';
import { toast } from 'react-toastify';
import { IOtherWithDescription } from '../../../../../__generated__/graphql';
import NumericInput from '../../../NumericInput';

const Container = styled.div`
  ${tw`relative border border-gray-500 w-full border-solid mt-4`}
`;

const ItemizationContainer = styled.div`
  ${tw`p-4`}
`;

const ItemizationHeader = styled.div`
  ${tw`bg-gray-100 p-2 font-bold border-b border-gray-600`}
`;

const ItemizationRow = styled.div`
  ${tw`flex justify-between p-2 border-b border-gray-600`}
`;

const Label = styled.div`
  ${tw`text-sm flex-grow`}
`;

const Amount = styled.div`
  ${tw`text-sm w-24 text-right`}
`;

const SubLabel = styled.div`
  ${tw`text-sm pl-4`}
`;

const EditingContainer = styled.div`
  ${tw`flex flex-col items-center w-full gap-8`}
  min-width: 1024px;
`;

const InputRow = styled.div`
  ${tw`flex flex-wrap w-full gap-4`}
`;

const InputGroup = styled.div`
  ${tw`flex flex-col`}
`;

const InputLabel = styled.label`
  ${tw`text-sm font-medium mb-2`}
`;

const StyledInput = styled.input`
  ${tw`border border-gray-300 rounded px-3 py-2 w-[240px]`}
`;

const ButtonContainer = styled.div`
  ${tw`flex justify-center w-full mt-6`}
`;

const SaveButton = styled(Button)`
  ${tw`bg-blue-500 text-white px-6 py-2 rounded`}
  &:hover {
    ${tw`bg-blue-600`}
  }
`;

const SectionTitle = styled.h3`
  ${tw`text-lg font-semibold mt-6 mb-3 text-gray-700`}
`;

const SubSectionTitle = styled.h4`
  ${tw`text-base font-medium mt-4 mb-2 text-gray-600`}
`;

interface ItemizationSectionProps {
  agreementId: string;
  agreementData: IFindCarSaleQuery | undefined;
}

const ItemizationSection = ({ agreementId, agreementData }: ItemizationSectionProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    ...agreementData?.carSale?.paymentDetails,
    cashPriceVehicleNAccessories: {
      amount: agreementData?.carSale?.paymentDetails?.cashPriceVehicleNAccessories?.amount ?? 0,
      cashPriceVehicle:
        agreementData?.carSale?.paymentDetails?.cashPriceVehicleNAccessories?.cashPriceVehicle ?? 0,
      cashPriceAccessories:
        agreementData?.carSale?.paymentDetails?.cashPriceVehicleNAccessories
          ?.cashPriceAccessories ?? 0,
      other: agreementData?.carSale?.paymentDetails?.cashPriceVehicleNAccessories?.other?.map(
        item => ({
          amount: item?.amount ?? 0,
        }),
      ) || [{ amount: 0, description: '' }],
    },
    docFee: agreementData?.carSale?.paymentDetails?.docFee ?? 0,
    smogFee: agreementData?.carSale?.paymentDetails?.smogFee ?? 0,
    theftDeterrentDevices:
      agreementData?.carSale?.paymentDetails?.theftDeterrentDevices?.map(item => ({
        paidTo: item?.paidTo ?? '',
        amount: item?.amount ?? 0,
      })) || Array(3).fill({ paidTo: '', amount: 0 }),
    surfaceProtectDevices:
      agreementData?.carSale?.paymentDetails?.surfaceProtectDevices?.map(item => ({
        paidTo: item?.paidTo ?? '',
        amount: item?.amount ?? 0,
      })) || Array(2).fill({ paidTo: '', amount: 0 }),
    evChargingStation: agreementData?.carSale?.paymentDetails?.evChargingStation?.map(item => ({
      paidTo: item?.paidTo ?? '',
      amount: item?.amount ?? 0,
    })) || [{ paidTo: '', amount: 0 }],
    salesTaxRate: agreementData?.carSale?.paymentDetails?.salesTaxRate ?? 0,
    salesTax: agreementData?.carSale?.paymentDetails?.salesTax ?? 0,
    evTransferFee: agreementData?.carSale?.paymentDetails?.evTransferFee?.map(item => ({
      paidTo: item?.paidTo ?? '',
      amount: item?.amount ?? 0,
    })) || [{ paidTo: '', amount: 0 }],
    serviceContacts:
      agreementData?.carSale?.paymentDetails?.serviceContacts?.map(item => ({
        paidTo: item?.paidTo ?? '',
        amount: item?.amount ?? 0,
      })) || Array(5).fill({ paidTo: '', amount: 0 }),
    priorCreditBySeller: agreementData?.carSale?.paymentDetails?.priorCreditBySeller?.map(item => ({
      paidTo: item?.paidTo ?? '',
      amount: item?.amount ?? 0,
    })) || [{ paidTo: '', amount: 0 }],
    debtCancellationAgreement:
      agreementData?.carSale?.paymentDetails?.debtCancellationAgreement ?? 0,
    usedVehicleContractCancellationOption:
      agreementData?.carSale?.paymentDetails?.usedVehicleContractCancellationOption ?? 0,
    others:
      agreementData?.carSale?.paymentDetails?.others?.map(item => ({
        paidTo: item?.paidTo ?? '',
        amount: item?.amount ?? 0,
        for: item?.for ?? '',
      })) || [],
    totalCashPrice: agreementData?.carSale?.paymentDetails?.totalCashPrice ?? 0,
    amtPaidToPublicOfficials: {
      vehicleLicenseFee:
        agreementData?.carSale?.paymentDetails?.amtPaidToPublicOfficials?.vehicleLicenseFee ?? 0,
      registerNTransferFee:
        agreementData?.carSale?.paymentDetails?.amtPaidToPublicOfficials?.registerNTransferFee ?? 0,
      caTireFee: agreementData?.carSale?.paymentDetails?.amtPaidToPublicOfficials?.caTireFee ?? 0,
      others:
        agreementData?.carSale?.paymentDetails?.amtPaidToPublicOfficials?.others?.map(item => ({
          paidTo: item?.paidTo ?? '',
          amount: item?.amount ?? 0,
          for: item?.for ?? '',
        })) || [],
      totalOfficialFee:
        agreementData?.carSale?.paymentDetails?.amtPaidToPublicOfficials?.totalOfficialFee ?? 0,
    },
    amtPaidToInsuranceComp: agreementData?.carSale?.paymentDetails?.amtPaidToInsuranceComp ?? 0,
    stateEmissionsCertFee: agreementData?.carSale?.paymentDetails?.stateEmissionsCertFee ?? 0,
    stateEmissionExemptionFee:
      agreementData?.carSale?.paymentDetails?.stateEmissionExemptionFee ?? 0,
    totalDownPayment: {
      totalTradeInVal:
        agreementData?.carSale?.paymentDetails?.totalDownPayment?.totalTradeInVal ?? 0,
      vehicleOneVal: agreementData?.carSale?.paymentDetails?.totalDownPayment?.vehicleOneVal ?? 0,
      vehicleTwoVal: agreementData?.carSale?.paymentDetails?.totalDownPayment?.vehicleTwoVal ?? 0,
      totalLessPriorCreditOrLeaseBalance:
        agreementData?.carSale?.paymentDetails?.totalDownPayment
          ?.totalLessPriorCreditOrLeaseBalance ?? 0,
      vehicleOneCredit:
        agreementData?.carSale?.paymentDetails?.totalDownPayment?.vehicleOneCredit ?? 0,
      vehicleTwoCredit:
        agreementData?.carSale?.paymentDetails?.totalDownPayment?.vehicleTwoCredit ?? 0,
      totalNetTradeInBal:
        agreementData?.carSale?.paymentDetails?.totalDownPayment?.totalNetTradeInBal ?? 0,
      vehicleOneBal: agreementData?.carSale?.paymentDetails?.totalDownPayment?.vehicleOneBal ?? 0,
      vehicleTwoBal: agreementData?.carSale?.paymentDetails?.totalDownPayment?.vehicleTwoBal ?? 0,
      deferredDownPayableToSeller:
        agreementData?.carSale?.paymentDetails?.totalDownPayment?.deferredDownPayableToSeller ?? 0,
      manufactureRebate:
        agreementData?.carSale?.paymentDetails?.totalDownPayment?.manufactureRebate ?? 0,
      cashEquivalent: agreementData?.carSale?.paymentDetails?.totalDownPayment?.cashEquivalent ?? 0,
      totalDownPayment:
        agreementData?.carSale?.paymentDetails?.totalDownPayment?.totalDownPayment ?? 0,
    },
    amountFinanced: agreementData?.carSale?.paymentDetails?.amountFinanced ?? 0,
  });

  useEffect(() => {
    if (agreementData?.carSale?.paymentDetails) {
      setPaymentDetails(
        prevState =>
          ({
            ...prevState,
            ...agreementData.carSale.paymentDetails,
            theftDeterrentDevices:
              agreementData?.carSale?.paymentDetails?.theftDeterrentDevices ||
              Array(3).fill({ paidTo: '', amount: 0 }),
            surfaceProtectDevices:
              agreementData?.carSale?.paymentDetails?.surfaceProtectDevices ||
              Array(2).fill({ paidTo: '', amount: 0 }),
            evChargingStation: agreementData?.carSale?.paymentDetails?.evChargingStation || [
              { paidTo: '', amount: 0 },
            ],
            evTransferFee: agreementData?.carSale?.paymentDetails?.evTransferFee || [
              { paidTo: '', amount: 0 },
            ],
            serviceContacts:
              agreementData?.carSale?.paymentDetails?.serviceContacts ||
              Array(5).fill({ paidTo: '', amount: 0 }),
            priorCreditBySeller: agreementData?.carSale?.paymentDetails?.priorCreditBySeller || [
              { paidTo: '', amount: 0 },
            ],
            others: agreementData?.carSale?.paymentDetails?.others || [],
            amtPaidToPublicOfficials: {
              ...agreementData?.carSale?.paymentDetails?.amtPaidToPublicOfficials,
              totalOfficialFee:
                agreementData?.carSale?.paymentDetails?.amtPaidToPublicOfficials
                  ?.totalOfficialFee ?? 0,
              others:
                agreementData?.carSale?.paymentDetails?.amtPaidToPublicOfficials?.others || [],
            },
            totalDownPayment: {
              ...agreementData?.carSale?.paymentDetails?.totalDownPayment,
              others: agreementData?.carSale?.paymentDetails?.totalDownPayment?.others || [],
            },
            amountFinanced: agreementData?.carSale?.paymentDetails?.amountFinanced ?? 0,
          }) as any,
      );
    }
  }, [agreementData]);

  const [UPDATE_CAR_SALE] = useUpdateCarSaleMutation({
    refetchQueries: [FIND_CAR_SALE],
    onCompleted: data => {
      setIsEditing(false);
      toast.success('Payment details updated successfully');
    },
    onError: error => {
      toast.error('Error updating payment details');
    },
  });

  const handleSave = async () => {
    // Remove __typename fields and prepare data for mutation
    const cleanPaymentDetails = {
      cashPriceVehicleNAccessories: {
        amount: paymentDetails.cashPriceVehicleNAccessories?.amount ?? 0,
        cashPriceVehicle: paymentDetails.cashPriceVehicleNAccessories?.cashPriceVehicle ?? 0,
        cashPriceAccessories:
          paymentDetails.cashPriceVehicleNAccessories?.cashPriceAccessories ?? 0,
        other:
          paymentDetails.cashPriceVehicleNAccessories?.other?.map(item => ({
            amount: item.amount ?? 0,
          })) || [],
      },
      docFee: paymentDetails.docFee ?? 0,
      smogFee: paymentDetails.smogFee ?? 0,
      theftDeterrentDevices:
        paymentDetails.theftDeterrentDevices?.map(item => ({
          paidTo: item.paidTo ?? '',
          amount: item.amount ?? 0,
        })) || [],
      surfaceProtectDevices:
        paymentDetails.surfaceProtectDevices?.map(item => ({
          paidTo: item.paidTo ?? '',
          amount: item.amount ?? 0,
        })) || [],
      evChargingStation:
        paymentDetails.evChargingStation?.map(item => ({
          paidTo: item.paidTo ?? '',
          amount: item.amount ?? 0,
        })) || [],
      salesTaxRate: paymentDetails.salesTaxRate ?? 0,
      salesTax: paymentDetails.salesTax ?? 0,
      evTransferFee:
        paymentDetails.evTransferFee?.map(item => ({
          paidTo: item.paidTo ?? '',
          amount: item.amount ?? 0,
        })) || [],
      serviceContacts:
        paymentDetails.serviceContacts?.map(item => ({
          paidTo: item.paidTo ?? '',
          amount: item.amount ?? 0,
        })) || [],
      priorCreditBySeller:
        paymentDetails.priorCreditBySeller?.map(item => ({
          paidTo: item.paidTo ?? '',
          amount: item.amount ?? 0,
        })) || [],
      debtCancellationAgreement: paymentDetails.debtCancellationAgreement ?? 0,
      usedVehicleContractCancellationOption:
        paymentDetails.usedVehicleContractCancellationOption ?? 0,
      others:
        paymentDetails.others?.map(item => ({
          paidTo: item.paidTo ?? '',
          amount: item.amount ?? 0,
          for: item.for ?? '',
        })) || [],
      totalCashPrice: paymentDetails.totalCashPrice ?? 0,
      amtPaidToPublicOfficials: {
        vehicleLicenseFee: paymentDetails.amtPaidToPublicOfficials?.vehicleLicenseFee ?? 0,
        registerNTransferFee: paymentDetails.amtPaidToPublicOfficials?.registerNTransferFee ?? 0,
        caTireFee: paymentDetails.amtPaidToPublicOfficials?.caTireFee ?? 0,
        others:
          paymentDetails.amtPaidToPublicOfficials?.others?.map(item => ({
            paidTo: item.paidTo ?? '',
            amount: item.amount ?? 0,
            for: item.for ?? '',
          })) || [],
        totalOfficialFee: paymentDetails.amtPaidToPublicOfficials?.totalOfficialFee ?? 0,
      },
      amtPaidToInsuranceComp: paymentDetails.amtPaidToInsuranceComp ?? 0,
      stateEmissionsCertFee: paymentDetails.stateEmissionsCertFee ?? 0,
      stateEmissionExemptionFee: paymentDetails.stateEmissionExemptionFee ?? 0,
      totalDownPayment: {
        totalTradeInVal: paymentDetails.totalDownPayment?.totalTradeInVal ?? 0,
        vehicleOneVal: paymentDetails.totalDownPayment?.vehicleOneVal ?? 0,
        vehicleTwoVal: paymentDetails.totalDownPayment?.vehicleTwoVal ?? 0,
        totalLessPriorCreditOrLeaseBalance:
          paymentDetails.totalDownPayment?.totalLessPriorCreditOrLeaseBalance ?? 0,
        vehicleOneCredit: paymentDetails.totalDownPayment?.vehicleOneCredit ?? 0,
        vehicleTwoCredit: paymentDetails.totalDownPayment?.vehicleTwoCredit ?? 0,
        totalNetTradeInBal: paymentDetails.totalDownPayment?.totalNetTradeInBal ?? 0,
        vehicleOneBal: paymentDetails.totalDownPayment?.vehicleOneBal ?? 0,
        vehicleTwoBal: paymentDetails.totalDownPayment?.vehicleTwoBal ?? 0,
        deferredDownPayableToSeller:
          paymentDetails.totalDownPayment?.deferredDownPayableToSeller ?? 0,
        manufactureRebate: paymentDetails.totalDownPayment?.manufactureRebate ?? 0,
        cashEquivalent: paymentDetails.totalDownPayment?.cashEquivalent ?? 0,
        totalDownPayment: paymentDetails.totalDownPayment?.totalDownPayment ?? 0,
      },
      amountFinanced: paymentDetails.amountFinanced ?? 0,
    };

    try {
      await UPDATE_CAR_SALE({
        variables: {
          input: {
            id: agreementId,
            paymentDetails: cleanPaymentDetails,
          },
        },
      });
      setIsEditing(false);
    } catch (error) {}
  };

  const renderEditModal = () => (
    <Modal isOpen={isEditing} onClose={() => setIsEditing(false)}>
      <EditingContainer>
        {/* 1. Total Cash Price Section */}
        <SectionTitle>1. Total Cash Price</SectionTitle>

        {/* A. Vehicle and Accessories */}
        <SubSectionTitle>A. Cash Price of Motor Vehicle and Accessories</SubSectionTitle>
        <InputRow>
          <InputGroup>
            <InputLabel>Total Amount</InputLabel>
            <StyledInput
              type="text"
              pattern="[0-9]*"
              value={paymentDetails?.cashPriceVehicleNAccessories?.amount ?? 0}
              onChange={e => {
                setPaymentDetails({
                  ...paymentDetails,
                  cashPriceVehicleNAccessories: {
                    ...paymentDetails.cashPriceVehicleNAccessories,
                    amount: Number(e.target.value || '0'),
                  },
                });
              }}
            />
          </InputGroup>
        </InputRow>

        <InputRow>
          <InputGroup>
            <InputLabel>1. Cash Price Vehicle</InputLabel>
            <StyledInput
              type="text"
              pattern="[0-9]*"
              value={paymentDetails?.cashPriceVehicleNAccessories?.cashPriceVehicle ?? 0}
              onChange={e => {
                setPaymentDetails({
                  ...paymentDetails,
                  cashPriceVehicleNAccessories: {
                    ...paymentDetails.cashPriceVehicleNAccessories,
                    cashPriceVehicle: Number(e.target.value || '0'),
                  },
                });
              }}
            />
          </InputGroup>

          <InputGroup>
            <InputLabel>2. Cash Price Accessories</InputLabel>
            <StyledInput
              type="text"
              pattern="[0-9]*"
              value={paymentDetails?.cashPriceVehicleNAccessories?.cashPriceAccessories ?? 0}
              onChange={e => {
                setPaymentDetails({
                  ...paymentDetails,
                  cashPriceVehicleNAccessories: {
                    ...paymentDetails.cashPriceVehicleNAccessories,
                    cashPriceAccessories: Number(e.target.value || '0'),
                  },
                });
              }}
            />
          </InputGroup>

          <InputGroup>
            <InputLabel>3. Other (Nontaxable) Describe</InputLabel>
            <StyledInput
              type="text"
              value={paymentDetails?.cashPriceVehicleNAccessories?.other?.[0]?.amount ?? ''}
              onChange={e => {
                const updatedOther = [
                  ...(paymentDetails?.cashPriceVehicleNAccessories?.other || []),
                ];
                updatedOther[0] = {
                  ...updatedOther[0],
                  amount: Number(e.target.value || '0'),
                };
                setPaymentDetails({
                  ...paymentDetails,
                  cashPriceVehicleNAccessories: {
                    ...(paymentDetails?.cashPriceVehicleNAccessories as any),
                    other: updatedOther,
                  },
                });
              }}
            />
          </InputGroup>

          <InputGroup>
            <InputLabel>Amount</InputLabel>
            <StyledInput
              type="text"
              pattern="[0-9]*"
              value={paymentDetails?.cashPriceVehicleNAccessories?.other?.[0]?.amount ?? 0}
              onChange={e => {
                const updatedOther = [
                  ...(paymentDetails?.cashPriceVehicleNAccessories?.other || []),
                ];
                updatedOther[0] = {
                  ...updatedOther[0],
                  amount: Number(e.target.value || '0'),
                };
                setPaymentDetails({
                  ...paymentDetails,
                  cashPriceVehicleNAccessories: {
                    ...(paymentDetails?.cashPriceVehicleNAccessories as any),
                    other: updatedOther,
                  },
                });
              }}
            />
          </InputGroup>
        </InputRow>

        {/* Continue with B through O sections */}
        <InputRow>
          <InputGroup>
            <InputLabel>B. Document Processing Charge</InputLabel>
            <StyledInput
              type="text"
              pattern="[0-9]*"
              value={paymentDetails?.docFee ?? 0}
              onChange={e => {
                setPaymentDetails({
                  ...(paymentDetails as any),
                  docFee: Number(e.target.value || '0'),
                });
              }}
            />
          </InputGroup>

          <InputGroup>
            <InputLabel>C. Emissions Testing Charge</InputLabel>
            <StyledInput
              type="text"
              pattern="[0-9]*"
              value={paymentDetails?.smogFee ?? 0}
              onChange={e => {
                setPaymentDetails({
                  ...(paymentDetails as any),
                  smogFee: Number(e.target.value || '0'),
                });
              }}
            />
          </InputGroup>
        </InputRow>

        {/* Theft Deterrent Devices */}
        <SubSectionTitle>D. (Optional) Theft Deterrent Device(s)</SubSectionTitle>
        {[1, 2, 3].map(index => (
          <InputRow key={`theft-${index}`}>
            <InputGroup>
              <InputLabel>{`${index}. Paid to`}</InputLabel>
              <StyledInput
                type="text"
                value={paymentDetails?.theftDeterrentDevices?.[index - 1]?.paidTo ?? ''}
                onChange={e => {
                  const updatedDevices = [...(paymentDetails?.theftDeterrentDevices || [])];
                  updatedDevices[index - 1] = {
                    ...updatedDevices[index - 1],
                    paidTo: e.target.value,
                  };
                  setPaymentDetails({
                    ...(paymentDetails as any),
                    theftDeterrentDevices: updatedDevices,
                  });
                }}
              />
            </InputGroup>
            <InputGroup>
              <InputLabel>Amount</InputLabel>
              <StyledInput
                type="text"
                pattern="[0-9]*"
                value={paymentDetails?.theftDeterrentDevices?.[index - 1]?.amount ?? 0}
                onChange={e => {
                  const updatedDevices = [...(paymentDetails?.theftDeterrentDevices || [])];
                  updatedDevices[index - 1] = {
                    ...updatedDevices[index - 1],
                    amount: Number(e.target.value || '0'),
                  };
                  setPaymentDetails({
                    ...(paymentDetails as any),
                    theftDeterrentDevices: updatedDevices,
                  });
                }}
              />
            </InputGroup>
          </InputRow>
        ))}

        {/* Surface Protection Products */}
        <SubSectionTitle>E. (Optional) Surface Protection Product(s)</SubSectionTitle>
        {[1, 2].map(index => (
          <InputRow key={`surface-${index}`}>
            <InputGroup>
              <InputLabel>{`${index}. Paid to`}</InputLabel>
              <StyledInput
                type="text"
                value={paymentDetails?.surfaceProtectDevices?.[index - 1]?.paidTo ?? ''}
                onChange={e => {
                  const updatedDevices = [...(paymentDetails?.surfaceProtectDevices || [])];
                  updatedDevices[index - 1] = {
                    ...updatedDevices[index - 1],
                    paidTo: e.target.value,
                  };
                  setPaymentDetails({
                    ...(paymentDetails as any),
                    surfaceProtectDevices: updatedDevices,
                  });
                }}
              />
            </InputGroup>
            <InputGroup>
              <InputLabel>Amount</InputLabel>
              <StyledInput
                type="text"
                pattern="[0-9]*"
                value={paymentDetails?.surfaceProtectDevices?.[index - 1]?.amount ?? 0}
                onChange={e => {
                  const updatedDevices = [...(paymentDetails?.surfaceProtectDevices || [])];
                  updatedDevices[index - 1] = {
                    ...updatedDevices[index - 1],
                    amount: Number(e.target.value || '0'),
                  };
                  setPaymentDetails({
                    ...(paymentDetails as any),
                    surfaceProtectDevices: updatedDevices,
                  });
                }}
              />
            </InputGroup>
          </InputRow>
        ))}

        {/* EV Charging Station */}
        <SubSectionTitle>F. EV Charging Station</SubSectionTitle>
        <InputRow>
          <InputGroup>
            <InputLabel>Paid to</InputLabel>
            <StyledInput
              type="text"
              value={paymentDetails?.evChargingStation?.[0]?.paidTo ?? ''}
              onChange={e => {
                setPaymentDetails({
                  ...(paymentDetails as any),
                  evChargingStation: [
                    {
                      ...paymentDetails?.evChargingStation?.[0],
                      paidTo: e.target.value,
                    },
                  ],
                });
              }}
            />
          </InputGroup>
          <InputGroup>
            <InputLabel>Amount</InputLabel>
            <StyledInput
              type="text"
              pattern="[0-9]*"
              value={paymentDetails?.evChargingStation?.[0]?.amount ?? 0}
              onChange={e => {
                setPaymentDetails({
                  ...(paymentDetails as any),
                  evChargingStation: [
                    {
                      ...paymentDetails?.evChargingStation?.[0],
                      amount: Number(e.target.value || '0'),
                    },
                  ],
                });
              }}
            />
          </InputGroup>
        </InputRow>

        {/* Sales Tax */}
        <SubSectionTitle>G. Sales Tax</SubSectionTitle>
        <InputRow>
          <InputGroup>
            <InputLabel>Sales Tax Rate (%)</InputLabel>
            <NumericInput
              value={paymentDetails?.salesTaxRate ?? 0}
              onValueChange={(value: number | null) => {
                setPaymentDetails({
                  ...paymentDetails,
                  salesTaxRate: value ?? 0,
                });
              }}
              width="240px"
            />
          </InputGroup>
          <InputGroup>
            <InputLabel>Sales Tax Amount</InputLabel>
            <NumericInput
              value={paymentDetails?.salesTax ?? 0}
              onValueChange={(value: number | null) => {
                setPaymentDetails({
                  ...paymentDetails,
                  salesTax: value ?? 0,
                });
              }}
              width="240px"
            />
          </InputGroup>
        </InputRow>

        {/* Electronic Vehicle Registration */}
        <SubSectionTitle>H. Electronic Vehicle Registration or Transfer Charge</SubSectionTitle>
        <InputRow>
          <InputGroup>
            <InputLabel>Amount</InputLabel>
            <StyledInput
              type="text"
              pattern="[0-9]*"
              value={paymentDetails?.evTransferFee?.[0]?.amount ?? 0}
              onChange={e => {
                setPaymentDetails({
                  ...(paymentDetails as any),
                  evTransferFee: [
                    {
                      paidTo: '',
                      amount: Number(e.target.value || '0'),
                    },
                  ],
                });
              }}
            />
          </InputGroup>
        </InputRow>

        {/* Service Contracts */}
        <SubSectionTitle>I. (Optional) Service Contract(s)</SubSectionTitle>
        {[1, 2, 3, 4, 5].map(index => (
          <InputRow key={`service-${index}`}>
            <InputGroup>
              <InputLabel>{`${index}. Paid to`}</InputLabel>
              <StyledInput
                type="text"
                value={paymentDetails?.serviceContacts?.[index - 1]?.paidTo ?? ''}
                onChange={e => {
                  const updatedContracts = [...(paymentDetails?.serviceContacts || [])];
                  updatedContracts[index - 1] = {
                    ...updatedContracts[index - 1],
                    paidTo: e.target.value,
                  };
                  setPaymentDetails({
                    ...paymentDetails,
                    serviceContacts: updatedContracts,
                  });
                }}
              />
            </InputGroup>
            <InputGroup>
              <InputLabel>Amount</InputLabel>
              <StyledInput
                type="text"
                pattern="[0-9]*"
                value={paymentDetails?.serviceContacts?.[index - 1]?.amount ?? 0}
                onChange={e => {
                  const updatedContracts = [...(paymentDetails?.serviceContacts || [])];
                  updatedContracts[index - 1] = {
                    ...updatedContracts[index - 1],
                    amount: Number(e.target.value || '0'),
                  };
                  setPaymentDetails({
                    ...paymentDetails,
                    serviceContacts: updatedContracts,
                  });
                }}
              />
            </InputGroup>
          </InputRow>
        ))}

        {/* 2. Amounts Paid to Public Officials */}
        <SectionTitle>2. Amounts Paid to Public Officials</SectionTitle>

        <InputRow>
          <InputGroup>
            <InputLabel>A. Vehicle License Fees</InputLabel>
            <StyledInput
              type="text"
              pattern="[0-9]*"
              value={paymentDetails?.amtPaidToPublicOfficials?.vehicleLicenseFee ?? 0}
              onChange={e => {
                setPaymentDetails({
                  ...paymentDetails,
                  amtPaidToPublicOfficials: {
                    ...paymentDetails.amtPaidToPublicOfficials,
                    vehicleLicenseFee: Number(e.target.value || '0'),
                  },
                });
              }}
            />
          </InputGroup>
        </InputRow>

        <InputRow>
          <InputGroup>
            <InputLabel>B. Registration/Transfer/Titling Fees</InputLabel>
            <StyledInput
              type="text"
              pattern="[0-9]*"
              value={paymentDetails?.amtPaidToPublicOfficials?.registerNTransferFee ?? 0}
              onChange={e => {
                setPaymentDetails({
                  ...paymentDetails,
                  amtPaidToPublicOfficials: {
                    ...paymentDetails.amtPaidToPublicOfficials,
                    registerNTransferFee: Number(e.target.value || '0'),
                  },
                });
              }}
            />
          </InputGroup>
        </InputRow>

        <InputRow>
          <InputGroup>
            <InputLabel>C. California Tire Fees</InputLabel>
            <StyledInput
              type="text"
              pattern="[0-9]*"
              value={paymentDetails?.amtPaidToPublicOfficials?.caTireFee ?? 0}
              onChange={e => {
                setPaymentDetails({
                  ...paymentDetails,
                  amtPaidToPublicOfficials: {
                    ...paymentDetails.amtPaidToPublicOfficials,
                    caTireFee: Number(e.target.value || '0'),
                  },
                });
              }}
            />
          </InputGroup>
        </InputRow>

        {/* Total Cash Price */}
        <SubSectionTitle>Total Cash Price</SubSectionTitle>
        <InputRow>
          <InputGroup>
            <InputLabel>Total Cash Price</InputLabel>
            <NumericInput
              value={paymentDetails?.totalCashPrice ?? 0}
              onValueChange={(value: number | null) => {
                setPaymentDetails({
                  ...paymentDetails,
                  totalCashPrice: value ?? 0,
                });
              }}
              width="240px"
            />
          </InputGroup>
        </InputRow>

        {/* Amount Financed */}
        <SectionTitle>Amount Financed</SectionTitle>
        <InputRow>
          <InputGroup>
            <InputLabel>Amount Financed</InputLabel>
            <NumericInput
              value={paymentDetails?.amountFinanced ?? 0}
              onValueChange={(value: number | null) => {
                setPaymentDetails({
                  ...paymentDetails,
                  amountFinanced: value ?? 0,
                });
              }}
              width="240px"
            />
          </InputGroup>
        </InputRow>

        {/* Total Down Payment Section */}
        <SubSectionTitle>Total Down Payment</SubSectionTitle>
        <InputRow>
          <InputGroup>
            <InputLabel>Total Down Payment Amount</InputLabel>
            <StyledInput
              type="text"
              pattern="[0-9]*"
              value={paymentDetails?.totalDownPayment?.totalDownPayment ?? 0}
              onChange={e => {
                setPaymentDetails({
                  ...paymentDetails,
                  totalDownPayment: {
                    ...paymentDetails.totalDownPayment,
                    totalDownPayment: Number(e.target.value || '0'),
                  },
                });
              }}
            />
          </InputGroup>

          <InputGroup>
            <InputLabel>Total Trade-In Value</InputLabel>
            <StyledInput
              type="text"
              pattern="[0-9]*"
              value={paymentDetails?.totalDownPayment?.totalTradeInVal ?? 0}
              onChange={e => {
                setPaymentDetails({
                  ...paymentDetails,
                  totalDownPayment: {
                    ...paymentDetails.totalDownPayment,
                    totalTradeInVal: Number(e.target.value || '0'),
                  },
                });
              }}
            />
          </InputGroup>
        </InputRow>

        <ButtonContainer>
          <SaveButton text="Save" onClick={handleSave} buttonWidth="240px" />
        </ButtonContainer>
      </EditingContainer>
    </Modal>
  );

  return (
    <Container onClick={() => setIsEditing(true)}>
      <ItemizationContainer>
        <ItemizationHeader>
          ITEMIZATION OF THE AMOUNT FINANCED (Seller may keep part of the amounts paid to others)
        </ItemizationHeader>

        <ItemizationRow>
          <Label>1. Total Cash Price</Label>
        </ItemizationRow>

        <ItemizationRow>
          <Label>A. Cash Price of Motor Vehicle and Accessories</Label>
          <Amount>
            ${Number(paymentDetails?.cashPriceVehicleNAccessories?.amount).toFixed(2)}
            (A)
          </Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>1. Cash Price Vehicle</SubLabel>
          <Amount>
            ${Number(paymentDetails?.cashPriceVehicleNAccessories?.cashPriceVehicle)?.toFixed(2)}
          </Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>2. Cash Price Accessories</SubLabel>
          <Amount>
            $
            {Number(paymentDetails?.cashPriceVehicleNAccessories?.cashPriceAccessories)?.toFixed(2)}
          </Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>3. Other (Nontaxable) Describe: _____</SubLabel>
          <Amount>
            $
            {paymentDetails?.cashPriceVehicleNAccessories?.other
              ?.map(item => Number(item.amount))
              .reduce((acc, curr) => acc + curr, 0)}
          </Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>4. Other (Nontaxable) Describe: _____</SubLabel>
          <Amount>N/A</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <Label>B. Document Processing Charge (not a governmental fee)</Label>
          <Amount>${Number(paymentDetails?.docFee)?.toFixed(2)} (B)</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <Label>C. Emissions Testing Charge (not a governmental fee)</Label>
          <Amount>${Number(paymentDetails?.smogFee)?.toFixed(2)} (C)</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <Label>D. (Optional) Theft Deterrent Device(s)</Label>
          <Amount>N/A</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>1. (paid to)</SubLabel>
          <Amount>N/A (D1)</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>2. (paid to)</SubLabel>
          <Amount>N/A (D2)</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>3. (paid to)</SubLabel>
          <Amount>N/A (D3)</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <Label>E. (Optional) Surface Protection Product(s)</Label>
          <Amount>N/A</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>1. (paid to)</SubLabel>
          <Amount>N/A (E1)</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>2. (paid to)</SubLabel>
          <Amount>N/A (E2)</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <Label>F. EV Charging Station (paid to)</Label>
          <Amount>N/A (F)</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <Label>G. Sales Tax (on taxable items in A through F)</Label>
          <Amount>
            ${Number(paymentDetails?.salesTax)?.toFixed(2)}
            (G)
          </Amount>
        </ItemizationRow>

        <ItemizationRow>
          <Label>H. Electronic Vehicle Registration or Transfer Charge</Label>
          <Amount>N/A (H)</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <Label>I. (Optional) Service Contract(s)</Label>
          <Amount>N/A</Amount>
        </ItemizationRow>

        {/* Service Contract sub-items */}
        {[1, 2, 3, 4, 5].map(num => (
          <ItemizationRow key={num}>
            <SubLabel>{num}. (paid to)</SubLabel>
            <Amount>N/A (I{num})</Amount>
          </ItemizationRow>
        ))}

        <ItemizationRow>
          <Label>J. Prior Credit or Lease Balance (e) paid by Seller to</Label>
          <Amount>N/A (J)</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>(see downpayment and trade-in calculation)</SubLabel>
          <Amount></Amount>
        </ItemizationRow>

        <ItemizationRow>
          <Label>K. Prior Credit or Lease Balance (e) paid by Seller to</Label>
          <Amount>N/A (K)</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <Label>
            L. (Optional) Debt Cancellation Agreement or Guaranteed Asset Protection Waiver
          </Label>
          <Amount>N/A (L)</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <Label>M. (Optional) Used Vehicle Contract Cancellation Option Agreement</Label>
          <Amount>N/A (M)</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <Label>N. Other paid to</Label>
          <Amount>N/A (N)</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <Label>O. Other paid to</Label>
          <Amount>N/A (O)</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <Label>Total Cash Price (A through O)</Label>
          <Amount>
            ${Number(paymentDetails?.totalCashPrice)?.toFixed(2)}
            (1)
          </Amount>
        </ItemizationRow>

        <ItemizationRow>
          <Label>2. Amounts Paid to Public Officials</Label>
          <Amount></Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>A. Vehicle License Fees</SubLabel>
          <Amount>
            ${Number(paymentDetails?.amtPaidToPublicOfficials?.vehicleLicenseFee)?.toFixed(2)} (A)
          </Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>B. Registration/Transfer/Titling Fees</SubLabel>
          <Amount>
            ${Number(paymentDetails?.amtPaidToPublicOfficials?.registerNTransferFee)?.toFixed(2)}
            (B)
          </Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>C. California Tire Fees</SubLabel>
          <Amount>
            ${Number(paymentDetails?.amtPaidToPublicOfficials?.caTireFee)?.toFixed(2)}
            (C)
          </Amount>
        </ItemizationRow>
        {paymentDetails?.amtPaidToPublicOfficials?.others?.map((other: IOtherWithDescription) => (
          <ItemizationRow>
            <SubLabel>{other.paidTo}</SubLabel>
            <SubLabel>{other.for}</SubLabel>
            <Amount>
              ${Number(other.amount)?.toFixed(2)}
              (D)
            </Amount>
          </ItemizationRow>
        ))}

        <ItemizationRow>
          <Label>Total Official Fees (A through D)</Label>
          <Amount>
            ${Number(paymentDetails?.amtPaidToPublicOfficials?.totalOfficialFee)?.toFixed(2)}
          </Amount>
        </ItemizationRow>

        <ItemizationRow>
          <Label>3. Amount Paid to Insurance Companies</Label>
          <SubLabel>(Total premiums from Statement of Insurance)</SubLabel>
          <Amount>N/A</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <Label>4. State Emissions Certification Fee or State Emissions Exemption Fee</Label>
          <Amount>N/A</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <Label>5. Subtotal (1 through 4)</Label>
          <Amount>${Number(paymentDetails?.totalCashPrice)?.toFixed(2)}</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <Label>6. Total Downpayment</Label>
          <Amount></Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>
            A. Total Agreed Value of Property Being Traded-In (see Trade-In Vehicle(s)):
          </SubLabel>
          <Amount>
            ${Number(paymentDetails?.totalDownPayment?.totalTradeInVal)?.toFixed(2)}
            (A)
          </Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>Vehicle 1 $_____ Vehicle 2 $_____</SubLabel>
          <Amount>
            ${Number(paymentDetails?.totalDownPayment?.vehicleOneVal)?.toFixed(2)}
            (A)
          </Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>B. Total Less Prior Credit or Lease Balance (e)</SubLabel>
          <Amount>
            ${Number(paymentDetails?.totalDownPayment?.vehicleTwoVal)?.toFixed(2)}
            (B)
          </Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>Vehicle 1 $_____ Vehicle 2 $_____</SubLabel>
          <Amount></Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>C. Total Net Trade-In (A-B)</SubLabel>
          <Amount>N/A (C)</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>Vehicle 1 $_____ Vehicle 2 $_____</SubLabel>
          <Amount></Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>D. Deferred Downpayment Payable to Seller</SubLabel>
          <Amount>N/A (D)</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>E. Manufacturer's Rebate</SubLabel>
          <Amount>N/A (E)</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>F. Other</SubLabel>
          <Amount>N/A (F)</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>G. Other</SubLabel>
          <Amount>N/A (G)</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>H. Other</SubLabel>
          <Amount>N/A (H)</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>I. Cash, Cash Equivalent, Check, Credit Card, or Debit Card</SubLabel>
          <Amount>N/A (I)</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <Label>Total Downpayment (C through I)</Label>
          <Amount>${Number(paymentDetails?.totalDownPayment?.totalDownPayment)?.toFixed(2)}</Amount>
        </ItemizationRow>

        <ItemizationRow>
          <SubLabel>
            (If negative, enter zero on line 6 and enter the amount less than zero as a positive
            number on line 1J and/or 1K above)
          </SubLabel>
          <Amount></Amount>
        </ItemizationRow>

        <ItemizationRow>
          <Label>7. Amount Financed (5 less 6)</Label>
          <Amount>${Number(paymentDetails?.amountFinanced ?? 0)?.toFixed(2)}</Amount>
        </ItemizationRow>
      </ItemizationContainer>
      {renderEditModal()}
    </Container>
  );
};

export default ItemizationSection;
